import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Target, BookOpen, Sparkles, Heart, Star } from 'lucide-react';

export const LessonPlan: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Lesson Overview */}
      <Card className="bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-2xl text-center text-purple-600 flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6" />
            Betty's Magical Friendship Adventure - A1/A2 Lesson Plan
            <Sparkles className="w-6 h-6" />
          </CardTitle>
          <div className="text-center">
            <Badge className="mr-2 bg-purple-100 text-purple-700">Ages 6-8</Badge>
            <Badge variant="outline" className="mr-2 bg-pink-100 text-pink-700">A1/A2 Level</Badge>
            <Badge variant="secondary" className="bg-blue-100 text-blue-700">60-90 minutes</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-lg mb-3 text-purple-600 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Story Context
              </h3>
              <p className="text-gray-700 mb-4">
                Young learners join Betty on a magical adventure where she discovers a enchanted 
                garden and makes friends with Luna the unicorn and Spark the dragon. Through 
                friendship and teamwork, they learn basic English vocabulary, colors, numbers, 
                and simple grammar in a fun, interactive way.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-3 text-pink-600 flex items-center gap-2">
                <Star className="w-5 h-5" />
                Learning Approach
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Story-based learning with magical characters
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Visual vocabulary with emojis and colors
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Interactive games and competitions
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Bilingual support (English/French)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Audio pronunciation practice
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Learning Objectives */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl text-green-600 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Learning Objectives for Ages 6-8
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3 text-blue-700">🌈 Vocabulary Objectives</h4>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Colors:</strong> blue, red, yellow, green, purple, pink, white, orange</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Numbers:</strong> one, two, three, four, five</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Fantasy Animals:</strong> unicorn, dragon, butterfly</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Adjectives:</strong> happy, sad, magical, beautiful, friendly, special</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Actions:</strong> open, find, dance, work, help, sparkle</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-purple-700">📝 Grammar & Skills Objectives</h4>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Simple Present:</strong> "Betty opens", "Butterflies dance"</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Verb "to be":</strong> "Betty is happy", "Luna is white"</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Counting:</strong> Basic number recognition and counting</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Listening:</strong> Audio comprehension with female voice</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Speaking:</strong> Pronunciation practice and repetition</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Age-Appropriate Features */}
      <Card className="border-2 border-yellow-200 bg-yellow-50">
        <CardHeader>
          <CardTitle className="text-xl text-yellow-700 flex items-center gap-2">
            🎯 Age-Appropriate Design (Ages 6-8)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg">
              <h5 className="font-semibold text-yellow-700 mb-2">🎨 Visual Learning</h5>
              <ul className="text-sm text-yellow-600 space-y-1">
                <li>• Large, colorful emojis for each word</li>
                <li>• Bright, engaging illustrations</li>
                <li>• Color-coded categories</li>
                <li>• Simple, clear layouts</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <h5 className="font-semibold text-yellow-700 mb-2">🎮 Interactive Elements</h5>
              <ul className="text-sm text-yellow-600 space-y-1">
                <li>• Click-to-hear pronunciation</li>
                <li>• Flip cards for vocabulary</li>
                <li>• Timed quiz games</li>
                <li>• Progress tracking with stars</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <h5 className="font-semibold text-yellow-700 mb-2">🌟 Motivation Features</h5>
              <ul className="text-sm text-yellow-600 space-y-1">
                <li>• Immediate positive feedback</li>
                <li>• Achievement badges</li>
                <li>• Friendly competition</li>
                <li>• Magical theme engagement</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assessment Rubric */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl text-red-600">Assessment Rubric for Young Learners</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-3 text-left font-semibold">Skills</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold text-green-700">Excellent ⭐⭐⭐</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold text-blue-700">Good ⭐⭐</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold text-yellow-700">Learning ⭐</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-3 font-medium">Vocabulary Recognition</td>
                  <td className="border border-gray-300 p-2 text-sm">Recognizes 20+ words correctly</td>
                  <td className="border border-gray-300 p-2 text-sm">Recognizes 10-19 words correctly</td>
                  <td className="border border-gray-300 p-2 text-sm">Recognizes 5-9 words correctly</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 font-medium">Color & Number Knowledge</td>
                  <td className="border border-gray-300 p-2 text-sm">Names all colors and numbers 1-5</td>
                  <td className="border border-gray-300 p-2 text-sm">Names most colors and numbers</td>
                  <td className="border border-gray-300 p-2 text-sm">Names some colors and numbers</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 font-medium">Listening Comprehension</td>
                  <td className="border border-gray-300 p-2 text-sm">Understands story completely</td>
                  <td className="border border-gray-300 p-2 text-sm">Understands main story points</td>
                  <td className="border border-gray-300 p-2 text-sm">Understands basic story elements</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 font-medium">Participation & Engagement</td>
                  <td className="border border-gray-300 p-2 text-sm">Actively participates in all activities</td>
                  <td className="border border-gray-300 p-2 text-sm">Participates in most activities</td>
                  <td className="border border-gray-300 p-2 text-sm">Participates with encouragement</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Lesson Structure */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl text-teal-600">Lesson Structure & Timing</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg">
              <div className="w-16 text-center">
                <Badge variant="outline" className="bg-purple-100 text-purple-700">10 min</Badge>
              </div>
              <div>
                <h5 className="font-semibold">🌟 Welcome & Introduction</h5>
                <p className="text-sm text-gray-600">Meet Betty and discover the magical door</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-pink-50 rounded-lg">
              <div className="w-16 text-center">
                <Badge variant="outline" className="bg-pink-100 text-pink-700">20 min</Badge>
              </div>
              <div>
                <h5 className="font-semibold">📖 Betty's Magical Story</h5>
                <p className="text-sm text-gray-600">Interactive storytelling with vocabulary learning</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
              <div className="w-16 text-center">
                <Badge variant="outline" className="bg-blue-100 text-blue-700">15 min</Badge>
              </div>
              <div>
                <h5 className="font-semibold">🎮 Grammar Games</h5>
                <p className="text-sm text-gray-600">Fun exercises with colors, numbers, and simple grammar</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-green-50 rounded-lg">
              <div className="w-16 text-center">
                <Badge variant="outline" className="bg-green-100 text-green-700">15 min</Badge>
              </div>
              <div>
                <h5 className="font-semibold">🃏 Magic Word Cards</h5>
                <p className="text-sm text-gray-600">Interactive flashcards with pronunciation practice</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-yellow-50 rounded-lg">
              <div className="w-16 text-center">
                <Badge variant="outline" className="bg-yellow-100 text-yellow-700">15 min</Badge>
              </div>
              <div>
                <h5 className="font-semibold">🏆 Friendship Quiz</h5>
                <p className="text-sm text-gray-600">Two-player competitive quiz about the story</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-3 bg-orange-50 rounded-lg">
              <div className="w-16 text-center">
                <Badge variant="outline" className="bg-orange-100 text-orange-700">10 min</Badge>
              </div>
              <div>
                <h5 className="font-semibold">🎨 Creative Homework</h5>
                <p className="text-sm text-gray-600">Drawing and coloring activity with vocabulary practice</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Learning Outcomes */}
      <Card className="bg-gradient-to-r from-green-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-xl text-green-600">Expected Learning Outcomes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-green-700 mb-3">By the end of this lesson, students will be able to:</h4>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>Recognize and name 8 basic colors in English</span>
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>Count from 1 to 5 in English</span>
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>Use simple present tense with "is/are"</span>
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>Understand and retell Betty's story</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-blue-700 mb-3">Additional Skills Developed:</h4>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <Heart className="w-4 h-4 text-blue-600 mt-0.5" />
                  <span>Listening skills through audio stories</span>
                </li>
                <li className="flex items-start gap-2">
                  <Heart className="w-4 h-4 text-blue-600 mt-0.5" />
                  <span>Pronunciation through repetition</span>
                </li>
                <li className="flex items-start gap-2">
                  <Heart className="w-4 h-4 text-blue-600 mt-0.5" />
                  <span>Cooperation through team activities</span>
                </li>
                <li className="flex items-start gap-2">
                  <Heart className="w-4 h-4 text-blue-600 mt-0.5" />
                  <span>Confidence in English speaking</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};